import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { PasswordInput } from '@/components/common/Input';
import { PASSWORD_VALIDATE_REGEX } from '@/configs/constants';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/common/Label';
import { Button } from '@/components/common/Button';
import { flowResult } from 'mobx';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
export default observer(function ChangePasswordPage() {
    // hooks
    const { t } = useTranslation();

    const { authStore: { changePassword } } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        currentPassword: yup
            .string()
            .required('form_error_validate.required'),
        newPassword: yup
            .string()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        confirmNewPassword: yup
            .string()
            .required('form_error_validate.required')
            .oneOf([yup.ref('newPassword')], 'form_error_validate.confirm_new_password_not_match')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        }
    });

    const onSubmit = async (data: FormData) => {
        const { currentPassword, newPassword } = data;
        const res = await flowResult(changePassword({ currentPassword, password: newPassword }));

        if (res) {
            form.reset();
            toastify('success', t('messages.change_password_success'));
        }
    };

    return (
        <div className='h-screen bg-background-2 md:h-auto'>
            <div className='pb-[1.5rem] bg-white'>
                <div className='px-4 py-6 title-3 hidden md:block md:px-4 md:py-6'>{t('words_title.change_password')}</div>
                <div className='px-4 pt-4 border-t border-input md:border-none md:pt-0'>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className='w-full'>
                            <div className='mb-[1rem]'>
                                <Label className='label-1' required>{t('words_title.current_password')}</Label>
                                <FormField
                                    control={form.control}
                                    name='currentPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showPassword ? 'password' : 'text'}
                                                    className='mt-[0.375rem]'
                                                    onToggleIcon={() => setShowPassword(!showPassword)}
                                                    isPreventCopy={true}
                                                    isPreventCut={true}
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='mb-[1rem]'>
                                <Label className='label-1' required>{t('words_title.new_password')}</Label>
                                <FormField
                                    control={form.control}
                                    name='newPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showNewPassword ? 'password' : 'text'}
                                                    className='mt-[0.375rem]'
                                                    onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                                    isPreventCopy={true}
                                                    isPreventCut={true}
                                                    {...field}
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        form.getValues('confirmNewPassword') && form.trigger('confirmNewPassword');
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage className='whitespace-pre-line' />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='mb-[1rem]'>
                                <Label className='label-1' required>{t('words_title.confirm_new_password')}</Label>
                                <FormField
                                    control={form.control}
                                    name='confirmNewPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <PasswordInput
                                                    type={!showConfirmPassword ? 'password' : 'text'}
                                                    className='mt-[0.375rem]'
                                                    onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    isPreventCopy={true}
                                                    isPreventCut={true}
                                                    {...field}
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        form.getValues('newPassword') && form.trigger('newPassword');
                                                    }}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className='flex justify-center'>
                                <Button type='submit' variant='submit' className='mt-6 min-w-60'>
                                    {t('button.change_password')}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
});
